import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, } from 'react';
import { SEE_MORE_LAYOUT_BUFFER, SEE_MORE_THRESHOLD, TOGGLE_COLLAPSE_DELAY_MS, } from '../constants/dualModeConstants';
const useDualModeResize = ({ dualSearchEnabled, renderCollapsed, isStreamingActive, initialHeight, finalInputHeight, finalMinExpandedHeight, measuredInputHeight, seeMoreButtonHeight, conversationContainerRef, leftColumnRef, contentRef, messagesContainerRef, streamingMessagesScrollRef, conversation, onToggleCollapse, }) => {
    const [showSeeMoreButton, setShowSeeMoreButton] = React.useState(false);
    // Persists across collapsed/expanded states - only show toggle if there's actually more content
    const [contentExceedsThreshold, setContentExceedsThreshold] = React.useState(false);
    const lastPostedHeightRef = useRef(null);
    const effectiveInputHeight = useMemo(() => Math.max(0, measuredInputHeight ?? finalInputHeight), [measuredInputHeight, finalInputHeight]);
    const collapsedContentMaxHeight = useMemo(() => {
        if (!dualSearchEnabled)
            return null;
        const baseHeight = initialHeight || 0;
        if (baseHeight <= 0)
            return null;
        return Math.max(0, baseHeight - effectiveInputHeight - (seeMoreButtonHeight ?? 0));
    }, [dualSearchEnabled, initialHeight, effectiveInputHeight, seeMoreButtonHeight]);
    const calculateContentHeight = useCallback((expanded = false) => {
        const measureContent = (el) => {
            const innerGrid = el.querySelector?.('.MuiGrid-container');
            // Ignore absolutely positioned children (e.g., scroll-down icon) to avoid inflating height
            const positionedChildren = (() => {
                const children = Array.from(el.children);
                return children.filter((child) => {
                    const pos = window.getComputedStyle(child).position;
                    return pos !== 'absolute' && pos !== 'fixed';
                });
            })();
            const childHeights = (() => {
                if (positionedChildren.length === 0)
                    return 0;
                const firstTop = positionedChildren[0]?.offsetTop ?? 0;
                const last = positionedChildren[positionedChildren.length - 1];
                const lastBottom = (last?.offsetTop ?? 0) + (last?.offsetHeight ?? 0);
                return Math.max(0, lastBottom - firstTop);
            })();
            const innerHeight = innerGrid?.scrollHeight || 0;
            const measuredChildren = childHeights || innerHeight;
            if (measuredChildren > 0) {
                return measuredChildren;
            }
            return el.scrollHeight || 0;
        };
        const refs = expanded
            ? [
                { ref: streamingMessagesScrollRef, name: 'streamingMessagesScrollRef' },
                { ref: messagesContainerRef, name: 'messagesContainerRef' },
            ]
            : [
                { ref: streamingMessagesScrollRef, name: 'streamingMessagesScrollRef' },
                { ref: messagesContainerRef, name: 'messagesContainerRef' },
                { ref: conversationContainerRef, name: 'conversationContainerRef' },
                { ref: contentRef, name: 'contentRef' },
                { ref: leftColumnRef, name: 'leftColumnRef' },
            ];
        let contentHeight = 0;
        let usingRef = 'none';
        refs.forEach(({ ref, name }) => {
            const el = ref.current;
            if (!el || !el.isConnected)
                return;
            const measured = measureContent(el);
            if (measured > contentHeight) {
                contentHeight = measured;
                usingRef = name;
            }
        });
        if (contentHeight === 0 && typeof document !== 'undefined' && !expanded) {
            const docHeight = Math.max(document.body?.scrollHeight || 0, document.body?.offsetHeight || 0, document.documentElement?.clientHeight || 0, document.documentElement?.scrollHeight || 0, document.documentElement?.offsetHeight || 0);
            if (docHeight > 0) {
                contentHeight = docHeight;
                usingRef = 'document';
            }
        }
        return { contentHeight, usingRef };
    }, [
        conversationContainerRef,
        contentRef,
        leftColumnRef,
        messagesContainerRef,
        streamingMessagesScrollRef,
    ]);
    const postHeight = useCallback((mode, height) => {
        const numericHeight = typeof height === 'number' ? height : Number(height);
        if (!Number.isFinite(numericHeight) || numericHeight <= 0) {
            return;
        }
        if (typeof window === 'undefined' || !window.parent) {
            return;
        }
        const roundedHeight = Math.ceil(numericHeight);
        if (mode === 'expanded' && roundedHeight === lastPostedHeightRef.current) {
            return;
        }
        // Avoid runaway loops: do not post if height barely changed (< 2px)
        if (mode === 'expanded' && lastPostedHeightRef.current !== null) {
            const delta = Math.abs(roundedHeight - lastPostedHeightRef.current);
            if (delta < 2) {
                return;
            }
        }
        lastPostedHeightRef.current = roundedHeight;
        window.parent.postMessage({
            source: 'microcommerce-ai-embed',
            type: 'expandIframe',
            mode,
            height: roundedHeight,
        }, '*');
    }, []);
    const computeExpandedHeight = useCallback((contentHeight) => {
        const baseHeight = typeof contentHeight === 'number'
            ? contentHeight
            : calculateContentHeight(true).contentHeight;
        if (!Number.isFinite(baseHeight) || baseHeight <= 0) {
            return null;
        }
        const hudHeight = effectiveInputHeight;
        // Include the See More/See Less control height so the parent (iframe) grows enough
        const seeMoreHeight = dualSearchEnabled ? Math.max(0, seeMoreButtonHeight ?? 64) : 0;
        const total = Math.max(baseHeight + hudHeight + seeMoreHeight + SEE_MORE_LAYOUT_BUFFER, finalMinExpandedHeight);
        return total;
    }, [
        calculateContentHeight,
        effectiveInputHeight,
        finalMinExpandedHeight,
        dualSearchEnabled,
        seeMoreButtonHeight,
    ]);
    const checkContentHeight = useCallback(() => {
        if (!dualSearchEnabled || collapsedContentMaxHeight === null) {
            setShowSeeMoreButton(false);
            // Only reset threshold when dual search is disabled (not when expanded)
            if (!dualSearchEnabled) {
                setContentExceedsThreshold(false);
            }
            return;
        }
        // Only measure and update threshold when COLLAPSED
        // When expanded, height: auto makes measurements unreliable for threshold detection
        if (!renderCollapsed) {
            return;
        }
        // Measure content height to update the threshold flag (only when collapsed)
        const { contentHeight } = calculateContentHeight(false);
        const thresholdValue = collapsedContentMaxHeight + SEE_MORE_THRESHOLD;
        const exceedsThreshold = contentHeight > thresholdValue;
        // Update persistent flag (used for button visibility in both states)
        setContentExceedsThreshold((prev) => (prev !== exceedsThreshold ? exceedsThreshold : prev));
        // Update showSeeMoreButton for backward compatibility
        setShowSeeMoreButton((prev) => (prev !== exceedsThreshold ? exceedsThreshold : prev));
    }, [dualSearchEnabled, renderCollapsed, collapsedContentMaxHeight, calculateContentHeight]);
    const syncExpandedHeight = useCallback(() => {
        if (!dualSearchEnabled || renderCollapsed) {
            return;
        }
        const nextHeight = computeExpandedHeight();
        if (nextHeight !== null) {
            postHeight('expanded', nextHeight);
        }
    }, [dualSearchEnabled, renderCollapsed, computeExpandedHeight, postHeight]);
    const handleToggleCollapse = useCallback(() => {
        const nextCollapsedState = !renderCollapsed;
        onToggleCollapse(nextCollapsedState);
        if (nextCollapsedState) {
            postHeight('collapsed', initialHeight);
            setTimeout(() => {
                checkContentHeight();
            }, TOGGLE_COLLAPSE_DELAY_MS);
        }
        else {
            syncExpandedHeight();
        }
    }, [
        renderCollapsed,
        onToggleCollapse,
        postHeight,
        initialHeight,
        checkContentHeight,
        syncExpandedHeight,
    ]);
    useLayoutEffect(() => {
        checkContentHeight();
    }, [checkContentHeight, conversation?.messages?.length]);
    useEffect(() => {
        if (!dualSearchEnabled || renderCollapsed) {
            return undefined;
        }
        syncExpandedHeight();
        const targets = [
            streamingMessagesScrollRef.current,
            messagesContainerRef.current,
        ].filter((el) => Boolean(el));
        if (targets.length === 0) {
            return undefined;
        }
        const handleResize = () => {
            syncExpandedHeight();
        };
        const resizeObserver = new ResizeObserver(handleResize);
        const mutationObserver = new MutationObserver(handleResize);
        targets.forEach((target) => {
            resizeObserver.observe(target);
            mutationObserver.observe(target, { childList: true, subtree: true });
        });
        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, [
        dualSearchEnabled,
        renderCollapsed,
        streamingMessagesScrollRef,
        messagesContainerRef,
        syncExpandedHeight,
    ]);
    const { contentHeight } = calculateContentHeight(!renderCollapsed);
    return {
        contentHeight,
        showSeeMoreButton,
        contentExceedsThreshold,
        handleToggleCollapse,
        isStreaming: !renderCollapsed && isStreamingActive,
        checkContentHeight,
        calculateContentHeight,
    };
};
export default useDualModeResize;
//# sourceMappingURL=useDualModeResize.js.map